import React, { FC, useState, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useCreateCompareArea, useFetchCompareAreas } from 'services/project'
import { AreaType, CreateAreaParams, Area } from 'models/project'
import { AreaTools, AreaSelection } from 'models/editor'
import { Dialog } from 'components/Dialog'
import { Stack } from 'components/Stack'
import { Flex } from 'components/Flex'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Button } from 'components/Button'
import { Icon } from 'components/Icon'
import { Confirmation } from '../shared/Confirmation'
import { IsochroneDialog } from '../shared/IsochroneDialog'
import { TextInput } from 'components/TextInput'
import { Polygon, PointCoordinates } from 'models/map'
import { RegionFormParams } from 'models/region'
import { sampleColors } from 'constants/sample-colors'
import { getCircleData } from 'utils/areaDetails'
import { useMapEditorContext } from 'context/map-editor-context'
import styles from './CompareAreaWizard.module.scss'
import { FileUploadDialog } from '../shared/FileUploadDialog'
import { RegionDialog } from '../shared/RegionDialog'

// Redux attempt
import { useDispatch } from 'react-redux'
import { fetchAreas } from 'actions/project'

export const CompareAreaWizard: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { areas = [] } = useFetchCompareAreas(projectId)
  const areasColors = areas.map((area) => area.color)
  const dispatch = useDispatch()

  const [createArea, { status }] = useCreateCompareArea(projectId)

  const [showDialog, setShowDialog] = useState(true)
  const [areaData, setAreaData] = useState<Partial<CreateAreaParams>>()

  const [{ areaTool, confirmed, editor }, mapEditorDispatch] = useMapEditorContext()
  const selectedFeature = editor.feature!

  const submitArea = (data: Partial<CreateAreaParams>) => {
    const createAreaData = {
      ...data,
      color: sampleColors.find((color) => !areasColors.includes(color)) || sampleColors[0],
    }

    createArea(createAreaData as CreateAreaParams, {
      onSuccess(area: Area) {
        mapEditorDispatch.featureCreated()
        navigate(`/projects/${projectId}/compare/${area.id}`)

        dispatch(fetchAreas(projectId))
      },
    })
  }

  const onNameFormSubmit = (event: FormEvent) => {
    event.preventDefault()
    areaData && submitArea(areaData)
  }

  // isochrones dialog
  const createPointArea = (data: CreateAreaParams) => {
    return setAreaData({
      ...data,
      coordinates: selectedFeature.geometry.coordinates as PointCoordinates,
    })
  }

  const createPolygon = (areaType: AreaType) => {
    mapEditorDispatch.setConfirm(true)
    setAreaData({
      areaType,
      geometry: {
        ...(selectedFeature.geometry as Polygon),
        crs: { type: 'name', properties: { name: 'EPSG:4326' } },
      },
    })
  }

  // With radius tool
  const createRadius = (areaType: AreaType) => {
    const { circleCenter, radius } = getCircleData(selectedFeature as any)
    mapEditorDispatch.setConfirm(true)
    setAreaData({
      areaType,
      coordinates: circleCenter as PointCoordinates,
      radius,
    })
  }

  const createRegion = (data: RegionFormParams) => {
    submitArea({
      areaType: AreaType.REGION,
      region: data,
    })
  }

  const onCancel = () => {
    setShowDialog(false)
    mapEditorDispatch.cancelWizard()
  }

  if (showDialog) {
    return (
      <Dialog aria-label={t('add_compare_area_dialog.title')} isOpen={true} onClose={onCancel}>
        <Stack space="medium">
          <div>
            <Heading size="secondary">{t('add_compare_area_dialog.title')}</Heading>
            <Text>{t('add_compare_area_dialog.desc')}</Text>
          </div>
          <Flex className={styles.icons} justify="center" gap="xxl">
            <Icon name="map-pin-line" size="xl" />
            <Icon name="shape-fill" size="xl" />
            <Icon name="globe-line" size="xl" />
            <Icon name="compass-2-line" size="xl" />
            <Icon name="file-upload-line" size="xl" />
          </Flex>
          <Button type="button" onClick={() => setShowDialog(false)} full>
            {t('add_compare_area_dialog.cta')}
          </Button>
        </Stack>
      </Dialog>
    )
  }

  if (areaData) {
    return (
      <Dialog
        isOpen={true}
        aria-label={t('area_name_dialog.title')}
        onClose={() => setAreaData(undefined)}
      >
        <Stack space="medium">
          <>
            <Heading size="secondary">{t('area_name_dialog.title')}</Heading>
            <Text>{t('area_name_dialog.desc')}</Text>
          </>
          <form onSubmit={onNameFormSubmit}>
            <Stack space="medium">
              <TextInput
                name="project_name"
                label={t('area_name_dialog.form.name')}
                value={areaData.name}
                onChange={(event) => setAreaData({ ...areaData, name: event.target.value })}
              />
              <Button full type="submit" loading={status === 'loading'}>
                {t('area_name_dialog.cta')}
              </Button>
            </Stack>
          </form>
        </Stack>
      </Dialog>
    )
  }

  if (areaTool && !confirmed) {
    const onConfirmFor = (areaTool: AreaSelection) => {
      switch (areaTool) {
        case AreaTools.RADIUS:
          return () => createRadius(AreaType.RADIUS)
        case AreaTools.POLYGON:
          return () => createPolygon(AreaType.POLYGON)
        default:
          return () => mapEditorDispatch.setConfirm(true)
      }
    }

    return <Confirmation onConfirm={onConfirmFor(areaTool)} />
  }

  if (areaTool === AreaTools.POINT) {
    return <IsochroneDialog onClose={onCancel} onConfirm={createPointArea} status={status} />
  }

  if (areaTool === AreaTools.REGION) {
    return (
      <RegionDialog
        onClose={() => mapEditorDispatch.featureCreated()}
        onConfirm={createRegion}
        status={status}
      />
    )
  }

  if (areaTool === AreaTools.FILE_UPLOAD) {
    return (
      <FileUploadDialog
        onClose={() => mapEditorDispatch.featureCreated()}
        onConfirm={submitArea}
        status={status}
      />
    )
  }

  return null
}

CompareAreaWizard.displayName = 'CompareAreaWizard'
